import { Component, inject, OnInit, Type, ViewChild } from '@angular/core';
import { HeaderComponent } from '../../common-components/header/header.component';
import { NgClass } from '@angular/common';
import { InfoModel } from '../../models/info.model';
import { SvgIconComponent } from '../../common-components/svg-icon/svg-icon.component';
import { OptionViewComponent } from './components/option-view/option-view.component';
import { NavigationBoxComponent } from '../../common-components/navigation-box/navigation-box.component';
import { ButtonType } from '../../common-components/fancy-button/button-type.enum';
import { ImageViewComponent } from './components/image-view/image-view.component';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { PopupComponent } from '../../common-components/popups/popup/popup.component';
import { QuestionListComponent } from '../../common-components/popups/question-list/question-list.component';
import { QuestionItem } from '../../models/question-item.model';
import { QuestionType } from '../../common-components/popups/question-list/question-type.enum';
import { QuestionService } from '../../services/question.service';
import { QuestionState } from './question-state.enum';
import { InputSelectComponent } from '../../common-components/input-select/input-select.component';
import { CommentComponent } from '../../common-components/comment/comment.component';
import { delay, forkJoin, of, switchMap, take, tap } from 'rxjs';
import { ConfirmationComponent } from '../../common-components/popups/confirmation/confirmation.component';
import { ConfirmationModel } from '../../models/confirmation.model';
import { ConfirmationButton } from '../../models/confirmation-button.model';
import { ConfirmationButtonType } from '../../common-components/popups/confirmation/confirmation-button-type.enum';
import { ActivatedRoute } from '@angular/router';
import { AssignmentService } from '../../services/assignment.service';
import { IdNameModel } from '../../models/idname.model';
import { GalleryService } from '../../services/gallery.service';
import { InformationComponent } from '../../common-components/popups/information/information.component';
import { InformationModel } from '../../models/information.model';
import { Deviation } from '../../interfaces/deviation.interface';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-audit-page',
  standalone: true,
  imports: [
    HeaderComponent,
    NgClass,
    SvgIconComponent,
    OptionViewComponent,
    NavigationBoxComponent,
    ImageViewComponent,
    FormsModule,
    PopupComponent,
    InputSelectComponent,
    ReactiveFormsModule,
    TranslocoDirective
  ],
  templateUrl: './audit-page.component.html',
  styleUrl: './audit-page.component.scss',
})
export class AuditPageComponent implements OnInit {
  protected selectedQuestionId: string = ""
  protected infoList: InfoModel[] = [];
  protected selectedIndex: number = 0
  protected questionList: QuestionItem[] = [];
  protected workstationList: IdNameModel[] = [];
  protected visibleBlock: QuestionState = QuestionState.FirstBlock;
  protected readOnly: boolean = false;
  protected progress: number = 0;
  protected questionsVisible: boolean = false;
  protected dynamicComponent: Type<any> = Object;
  protected popupData: any;
  protected slideInFrom: string = '';
  protected readonly QuestionState = QuestionState;
  protected commentControl: FormControl<string> = new FormControl<string>('', { nonNullable: true })
  protected workstationFormGroup = new FormGroup({
    id: new FormControl<string>('', {validators: [Validators.required]}),
    name: new FormControl<string>('', {validators: [Validators.required]})
    });
  protected showErrorMessage: boolean = false;
  protected isReady = false;
  protected isDuplicating = false;

  @ViewChild(PopupComponent) popupComponent!: PopupComponent;
  private readonly doneOptionId: number = 9;
  private taskLayoutId = ""
  private finalizeInProgress = false;

  private route = inject(ActivatedRoute);
  private questionService = inject(QuestionService);
  private assignmentsService = inject(AssignmentService);
  private galleryService = inject(GalleryService);
  private translocoService = inject(TranslocoService);

  ngOnInit() {
    this.isReady = false;
    this.route.params.pipe(
      tap(params => this.taskLayoutId = params['id']),
      switchMap(() => this.assignmentsService.getAssignments()),
      switchMap(() => this.assignmentsService.getTaskList(this.taskLayoutId)),
      switchMap((taskList) => forkJoin({
        taskList: of(taskList),
        isFinished: this.assignmentsService.taskLayoutIsFinished(this.taskLayoutId),
        translocoReady: this.translocoService.selectTranslation().pipe(take(1))
      })),
      tap(({ isFinished }) => {
        const isReallyFinished = typeof isFinished === 'object' ? (isFinished as any).isFinished : isFinished;

        if (isReallyFinished || this.assignmentsService.isOverdue(this.taskLayoutId)) {
          this.visibleBlock = QuestionState.ReadOnlyView;
          this.readOnly = true;
        } else {
          this.visibleBlock = QuestionState.FirstBlock;
          this.readOnly = false;
        }
      })
    ).subscribe(({ taskList }) => {
      taskList.forEach((task: QuestionItem) => {
        task.stateOptions = this.createStateOptions(task);
        this.initWorkstation(task);
        this.questionList.push(task);
      });

      this.workstationFormGroup.valueChanges.subscribe((value) => {
        if (value.id) {
          this.questionList[this.selectedIndex].workstation = value as IdNameModel;
        } else {
          this.questionList[this.selectedIndex].workstation = null;
        }
      });

      this.questionService.selectedId.subscribe((id: string) => {
        if (!!id) {
          this.selectedQuestionId = id;
          this.selectedIndex = this.questionList.findIndex(q => q.id === this.selectedQuestionId);
          this.setupSelected();
        } else {
          this.questionService.selectedId.next(this.questionList[this.selectedIndex].id)
        }
      });
      this.selectedQuestionId = this.questionService.selectedId.value;
      this.selectedIndex = this.questionList.findIndex(q => q.id === this.questionService.selectedId.value);
      if (this.selectedIndex === -1) {
        this.selectedIndex = 0;
        this.questionService.selectedId.next(this.questionList[this.selectedIndex].id)
      }
      this.isReady = true;
    });
  }

  protected selectedQuestion(): QuestionItem {
    return this.questionList.find(q => q.id === this.selectedQuestionId) ?? this.questionList[0];
  }

  protected showQuestionsPopUp(): void {
    this.popupData = this.questionList;
    this.dynamicComponent = QuestionListComponent;
    this.questionsVisible = !this.questionsVisible;
    this.slideInFrom = 'translateY(-100%)';
  }

  protected showDescription() {
    this.popupData = this.selectedQuestion().comment;
    this.dynamicComponent = CommentComponent;
    this.questionsVisible = !this.questionsVisible;
    this.slideInFrom = '';
  }

  protected showConfirmation() {
    this.dynamicComponent = ConfirmationComponent;
    this.questionsVisible = !this.questionsVisible;
    this.slideInFrom = '';
    if (this.isUnfinished()) {
      this.openIncompletePopup();
    } else {
      this.openCompletePopup();
    }
  }

  protected showInfirmation() {
    this.dynamicComponent = InformationComponent;
    this.slideInFrom = '';
    this.questionsVisible = !this.questionsVisible;
    this.openInformationPopup()
  }

  protected back(): void {
    if (this.readOnly) {
      this.previousQuestion();
      return;
    }
    this.visibleBlock--;

    if (this.visibleBlock < 0) {
      this.previousQuestion();
      return;
    }

    this.setProgress();
  }

  protected next(): void {
    const question = this.selectedQuestion();
    const doneOption = question.deviations.find(d => this.statusIsDone(d));

    if (!this.nextIsAvailable()) {
      if (this.visibleBlock == QuestionState.SecondBlock && question.deviation?.id !== doneOption?.id) {
        this.workstationFormGroup.markAsTouched();
        this.workstationFormGroup.updateValueAndValidity();
      }
      this.showErrorMessage = true;
      return;
    }
    if (this.visibleBlock == QuestionState.SecondBlock && question.deviation?.id === doneOption?.id) {
      this.workstationFormGroup.reset();
      this.workstationFormGroup.updateValueAndValidity();
    }

    this.showErrorMessage = false;
    if (this.readOnly) {
      this.nextQuestion();
      return;
    }
    this.setCurrentState();

    if ((this.visibleBlock === QuestionState.FirstBlock
        && question.stateOptions.filter(o => o.selected).every(x =>
          x.name === this.translocoService.translate('STATES.OK')
          || x.name === this.translocoService.translate('STATES.NOT-RELEVANT')))
      || (this.visibleBlock === QuestionState.SecondBlock && question.deviation?.id === doneOption?.id)
      || this.visibleBlock === QuestionState.ThirdBlock) {
      this.nextQuestion();
    } else {
      this.visibleBlock++;
      this.setProgress();
    }

    if (this.visibleBlock === QuestionState.SecondBlock && doneOption) {
      question.deviation = doneOption;
    }

    this.setInfoList(question);
  }

  protected duplicationIsDisabled():boolean {
    return this.isDuplicating || !this.nextIsAvailable();
  }

  protected nextIsAvailable(): boolean {
    let question = this.questionList[this.selectedIndex];
    const doneOption = this.statusIsDone(question.deviation);
    if (this.visibleBlock === QuestionState.FirstBlock
      && question.stateOptions.filter(o => o.selected).length
      && !this.finalizeInProgress) {
      return true;
    }
    if (this.visibleBlock === QuestionState.SecondBlock
      && question.deviation
      && (this.workstationFormGroup.valid || doneOption)
      && !this.finalizeInProgress) {
      if (!this.workstationFormGroup.valid) {
        this.workstationFormGroup.reset();
      }
      return true;
    }
    if (this.visibleBlock === QuestionState.ThirdBlock) {
      const { isNok, hasDeviation, hasImage } = this.getQuestionStatus();
      return (!isNok || !hasDeviation || hasImage) && !this.finalizeInProgress;
    }
    return (this.visibleBlock === QuestionState.ReadOnlyView && this.selectedIndex !== this.questionList.length - 1);
  }

  protected buttonText(): string {
    let stateOptions = this.questionList[this.selectedIndex].stateOptions.filter(o => o.selected);
    let deviation = this.questionList[this.selectedIndex].deviation;
    if (
      this.visibleBlock === QuestionState.ThirdBlock
      || (this.visibleBlock === QuestionState.FirstBlock
        && stateOptions.length && stateOptions.every(x =>
          x.name === this.translocoService.translate('STATES.OK')
            || x.name === this.translocoService.translate('STATES.NOT-RELEVANT')))
      || (this.visibleBlock === QuestionState.SecondBlock
        && deviation && this.statusIsDone(deviation))
    ) {
      return (this.selectedIndex == this.questionList.length - 1) ? 'AUDIT-PAGE.BUTTONS.FINISH' : 'AUDIT-PAGE.BUTTONS.NEXT-TOPIC';
    }
    return "COMMON.LABEL.NEXT";
  }

  protected duplicateQuestion() {
    if (this.duplicationIsDisabled()) return;

    this.isDuplicating = true;

    this.assignmentsService.saveQuestionResult(this.questionList[this.selectedIndex]).pipe(
      switchMap(() =>
        this.assignmentsService.duplicateQuestionTask(this.questionList[this.selectedIndex].id)
      ),
      switchMap(() =>
        this.assignmentsService.getTaskList(this.taskLayoutId)
      )
    ).subscribe({
      next: (updatedTaskList: QuestionItem[]) => {
        this.questionList = [];
        updatedTaskList.forEach((task: QuestionItem) => {
          task.stateOptions = this.createStateOptions(task);
          this.initWorkstation(task);
          this.questionList.push(task);
        });

        const newIndex = this.selectedIndex + 1;
        if (newIndex < this.questionList.length) {
          this.selectedIndex = newIndex;
          this.selectedQuestionId = this.questionList[newIndex].id;
        } else {
          this.selectedIndex = this.questionList.length - 1;
          this.selectedQuestionId = this.questionList[this.selectedIndex].id;
        }

        this.questionService.selectedId.next(this.selectedQuestionId);
      },
      error: (err) => {
        this.isDuplicating = false;
      },
      complete: () => {
        this.isDuplicating = false;
      }
    });
  }

  protected nextQuestion() {
    if (this.selectedIndex === this.questionList.length - 1) {
      if (!this.readOnly) {
        this.assignmentsService.saveQuestionResult(this.questionList[this.selectedIndex]).subscribe();
        this.showConfirmation();
      }
    } else {
      if (!this.readOnly) {
        this.assignmentsService.saveQuestionResult(this.questionList[this.selectedIndex]).subscribe();
      }
      this.selectedIndex++;
      this.questionService.selectedId.next(this.questionList[this.selectedIndex].id);
    }
  }

  protected previousQuestion() {
    this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
    this.questionService.selectedId.next(this.questionList[this.selectedIndex].id)
  }

  protected backIsActive(): boolean {
    return !(this.selectedIndex === 0 && this.visibleBlock === 0)
  }

  protected shortText(text: string, length: number) {
    if (text?.length > length) {
      return text.slice(0, length - 3) + '...';
    }
    return text;
  }

  protected setClass(value: string): string {
    switch (value) {
      case this.translocoService.translate('STATES.OK'):
        return 'ok';
      case this.translocoService.translate('STATES.NOK'):
        return 'nok';
      case this.translocoService.translate('STATES.NOT-RELEVANT'):
        return 'other';
      default:
        return '';
    }
  }

  private getQuestionStatus(): { isNok: boolean, hasDeviation: boolean, hasImage: boolean } {
    const question = this.selectedQuestion();

    const isNok = question.stateOptions.some(o => o.selected && o.name === 'NOK');
    const hasDeviation = !!question.deviation && !this.statusIsDone(question.deviation);
    const hasImage = question.images && question.images.length > 0;

    return { isNok, hasDeviation, hasImage };
  }

  private openCompletePopup() {
    let confirmationModel = new ConfirmationModel({
      imagePath: 'assets/confirm-complete.svg',
      prompt: this.translocoService.translate('POPUPS.COMPLETE.TITLE'),
      hint: this.translocoService.translate('POPUPS.COMPLETE.HINT'),
      buttons: [
        new ConfirmationButton({
          text: this.translocoService.translate('COMMON.LABEL.CANCEL'),
          type: ConfirmationButtonType.Cancel
        }),
        new ConfirmationButton({
          text: this.translocoService.translate('POPUPS.COMPLETE.FINISH'),
          type: ConfirmationButtonType.Confirm
        })
      ]
    });
    this.popupData = confirmationModel;
    this.popupComponent.confirmed.pipe(take(1)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.finalizeInProgress = true;
        this.assignmentsService.finishTaskLayout(this.taskLayoutId).subscribe(
          {
            next: () => {
              // This executes ONLY on success
              this.visibleBlock = QuestionState.ReadOnlyView;
              this.readOnly = true;
              confirmationModel = this.openSuccessPopup();
            },
            error: () => {
              this.finalizeInProgress = false;
            }
          })
      }
    });
  }

  private openSuccessPopup() {
    let confirmationModel = new ConfirmationModel({
      imagePath: 'assets/completion.svg',
      prompt: this.translocoService.translate('POPUPS.SUCCESS.TITLE'),
      hint: this.translocoService.translate('POPUPS.SUCCESS.HINT'),
      buttons: [
        new ConfirmationButton({
          text: this.translocoService.translate('COMMON.LABEL.HOME'),
          type: ConfirmationButtonType.Confirm,
          target: ''
        })
      ]
    })
    this.popupData = confirmationModel;
    this.questionsVisible = !this.questionsVisible;
    return confirmationModel;
  }

  private openIncompletePopup() {
    let confirmationModel = new ConfirmationModel({
      imagePath: 'assets/incomplete.svg',
      prompt: this.translocoService.translate('POPUPS.INCOMPLETE.TITLE'),
      hint: this.translocoService.translate('POPUPS.INCOMPLETE.HINT'),
      buttons: [
        new ConfirmationButton({
          text: this.translocoService.translate('COMMON.LABEL.OKAY'),
          type: ConfirmationButtonType.Confirm
        })]
    });
    this.popupData = confirmationModel;
    this.popupComponent.confirmed.pipe(take(1)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.findUnfinishedState();
      }
    });
  }

  private openInformationPopup() {
    let informationModel = new InformationModel({
      chapter: this.selectedQuestion().name,
      question: this.selectedQuestion().description
    })
    this.popupData = informationModel;
  }

  private setProgress() {
    const all = this.questionList.length;
    if (all === 0) {
      this.progress = 0;
      return;
    }
    let answered = this.questionList.filter(x => x.state !== QuestionType.Unknown).length;
    this.progress = answered / all * 100;

  }

  private setCurrentState(): void {
    let question = this.questionList[this.selectedIndex];
    let doneOption = question.deviations.find(x => this.statusIsDone(x));
    let type = question.stateOptions.filter(o => o.selected)[0].type ?? ButtonType.Default;
    switch (type) {
      case ButtonType.Success:
        question.state = QuestionType.Ok;
        if (doneOption) {
          question.deviation = doneOption;
        }
        break;
      case ButtonType.Error:
        question.state = QuestionType.Nok;
        break;
      case ButtonType.Warning:
        question.state = QuestionType.NotRelevant;
        if (doneOption) {
          question.deviation = doneOption;
        }
        break;
      default:
        question.state = QuestionType.Unknown;
        break;
    }
  }

  private setupSelected() {
    let question = this.questionList[this.selectedIndex];
    if (this.readOnly) {
      this.visibleBlock = QuestionState.ReadOnlyView;
    } else if (question.state !== QuestionType.Nok ) {
      this.visibleBlock = QuestionState.FirstBlock;
    } else if (this.statusIsDone(question.deviation)) {
      this.visibleBlock = QuestionState.SecondBlock;
    } else if (this.selectedIndex === -1) {
      return;
    } else {
      this.visibleBlock = QuestionState.ThirdBlock;
    }

    this.workstationFormGroup.setValue(question.workstation ?? new IdNameModel());
    this.workstationFormGroup.markAsUntouched();
    this.commentControl.setValue(question.comment);
    this.galleryService.images = [...question.images]
    this.setProgress();
    this.setInfoList(question);
  }

  private isUnfinished(): boolean {
    return this.questionList.some(q =>
      this.stateIsMissing(q) || this.deviationIsMissing(q) || this.imagesAreMissing(q))
  }

  private findUnfinishedState(): void {
    let unfinishedIndices =
      [
        this.questionList.findIndex(q => this.stateIsMissing(q)),
        this.questionList.findIndex(q => this.deviationIsMissing(q)),
        this.questionList.findIndex(q => this.imagesAreMissing(q))
      ];
    let firstUnfinished = Math.min(...unfinishedIndices.filter(x => x !== -1));
    this.questionService.selectedId.next(this.questionList[firstUnfinished].id);
    this.visibleBlock = unfinishedIndices.findIndex(x => x === firstUnfinished)
    this.setProgress()
  }

  private stateIsMissing(q: QuestionItem):boolean {
    return q.state === QuestionType.Unknown
  }

  private deviationIsMissing(q: QuestionItem):boolean {
    return !q.deviation || (!this.statusIsDone(q.deviation) && !q.workstation)
  }

  private imagesAreMissing(q: QuestionItem): boolean {
    return !this.statusIsDone(q.deviation) && (!q.images.length)
  }

  private createStateOptions(task: QuestionItem) {
    return [
      {
        name: this.translocoService.translate('STATES.OK'),
        selected: task.state === QuestionType.Ok,
        icon: 'tick.svg',
        type: ButtonType.Success
      },
      {
        name: this.translocoService.translate('STATES.NOK'),
        selected: task.state === QuestionType.Nok,
        icon: 'x.svg',
        type: ButtonType.Error
      },
      {
        name: this.translocoService.translate('STATES.NOT-RELEVANT'),
        selected: task.state === QuestionType.NotRelevant,
        icon: 'minus.svg',
        type: ButtonType.Warning
      }];
  }

  private initWorkstation(task: QuestionItem) {
    if (this.workstationList.length == 0) {
      if (task.workstations?.length > 0) {
        task.workstations.forEach((workstation: IdNameModel) => {
          this.workstationList.push({ id: workstation.id, name: workstation.name });
        })
      } else {
        this.workstationList.push({ id: '', name: 'unkown' });
      }
      this.workstationList.sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true }))
    }
  }

  private setInfoList(question: QuestionItem) {
    this.infoList = [{ name: 'AUDIT-PAGE.LABELS.WORKSTATION', values: [question.workstation?.name ?? ''] }]

    const stateValues = question.stateOptions
      .filter(o => o.selected)
      .map(o => o.name);

    if (question.deviation && question.deviation.label) {
      stateValues.push(question.deviation.label);
    }

    if (stateValues.length) {
      this.infoList.push({
        name: 'AUDIT-PAGE.LABELS.STATUS',
        values: stateValues
      });
    }
  }

  private statusIsDone(deviation: Deviation | undefined | null): boolean {
    return deviation?.id === this.doneOptionId;
  }
}
